/**
 * Pre-authorization gate for self-service sign-up: an email may only
 * register if a Manager already pre-created a placeholder profile for it
 * (My Team's "Add New Athlete"). See backend/app/api/v1/users.py's
 * check_preauthorized/claim_preauthorized for the server-side logic.
 */
import { getBackendUrl } from '@/services/aeroApi'
import { authenticatedFetch } from '@/services/backendAuth'

export interface PreauthorizationStatus {
  authorized: boolean
  already_registered: boolean
}

/**
 * Called before any Firebase Auth account exists, so this is a plain,
 * unauthenticated request -- not authenticatedFetch, which requires a
 * signed-in user.
 */
export async function checkPreauthorized(email: string): Promise<PreauthorizationStatus> {
  const res = await fetch(`${getBackendUrl()}/api/v1/check-preauthorized`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email })
  })
  if (!res.ok) {
    throw new Error('Could not verify authorization for this email.')
  }
  return res.json()
}

/**
 * Called immediately after createUserWithEmailAndPassword succeeds --
 * migrates the pre-authorized placeholder profile (name, sport, weight,
 * Team/Squad membership) onto the caller's new real uid. Throws on failure;
 * the caller must delete the just-created Auth account and sign out (the
 * email was never actually pre-authorized, or was already claimed).
 */
export async function claimPreauthorizedAccount(): Promise<void> {
  const res = await authenticatedFetch(`${getBackendUrl()}/api/v1/claim-preauthorized`, {
    method: 'POST'
  })
  if (!res.ok) {
    const errData = await res.json().catch(() => ({} as any))
    throw new Error(errData.detail || 'This email has not been pre-authorized.')
  }
}
